// 지원 탭 규칙(계약 SUPPORT 5·9 화면 약속): 진입 코드 읽기·배너 문구·끝 사유 문구·갱신 한 번만·지원 탭에서 멈출 것.
// admin-hospital supportSession.js 사본(리셀러 콘솔 지원 탭, 10-29) — 다른 것은 SUPPORT_TAB_BLOCKED 목록. 순수 모듈(tests/supportSession.test.mjs).

export const SUPPORT_ENTER_PATH = '/support/enter';
export const SUPPORT_ENDED_PATH = '/support/ended';
/** 지원 진입·끝 화면인가 — 이 화면들은 일반 로그인 토큰을 실어 부르지 않는다 */
export const isSupportPagePath = (path) =>
  path === SUPPORT_ENTER_PATH || path === SUPPORT_ENDED_PATH;
/** 끝 화면 주소(사유는 SUPPORT_END_REASONS 코드 — 개인정보 아님) */
export const supportEndedUrl = (reason) =>
  `${SUPPORT_ENDED_PATH}?reason=${encodeURIComponent(reason || 'ENDED')}`;

/** 주소 조각 "#c=<코드>&to=<경로>" → {code, to}. 없으면 빈 문자열 */
export const readHashParams = (hash = '') => {
  const p = new URLSearchParams(String(hash).replace(/^#/, ''));
  return { code: p.get('c') || '', to: p.get('to') || '' };
};

/**
 * 앱 안 경로만(/ 로 시작, 둘째 글자가 / 나 \ 아님, \·제어 문자 없음 — 브라우저가 탭·줄바꿈을 지워 //바깥 주소가 되는 것도 막음).
 * 아니면 fallback
 */
export const safeInternalPath = (to, fallback = '/') =>
  // eslint-disable-next-line no-control-regex
  typeof to === 'string' && /^\/(?![/\\])[^\\\x00-\x1f\x7f]*$/.test(to)
    ? to
    : fallback;

// SUPPORT_END_REASONS(계약 support-scope.ts) → 쉬운 말
export const SUPPORT_END_REASON_TEXT = {
  NO_SESSION: '지원 접속 기록이 없습니다',
  MISMATCH: '접속 정보가 맞지 않습니다',
  ENDED: '종료되었습니다',
  EXPIRED: '최대 8시간이 지났습니다',
  IDLE: '30분 동안 쓰지 않았습니다',
  CONSENT_OFF: '고객사가 「고객 지원을 위한 접근 허용」을 껐습니다',
  RESELLER_UNLINKED: '리셀러 연동이 끊겼습니다',
  SUB_ACCOUNT_LOCKED: '지원 계정이 잠겼습니다',
  ACTOR_INACTIVE: '접속한 사람의 계정이 정지되었거나 삭제되었습니다',
  ACTOR_NO_PERMISSION: '접속한 사람의 지원 권한이 없어졌습니다',
  SUPPORT_ACCESS_OFF: '지원 접속 기능이 꺼졌습니다',
  INTROSPECT_UNAVAILABLE: '인증 서버에서 답을 받지 못했습니다',
  ACCOUNT_INACTIVE: '계정이 정지되었거나 삭제되었습니다',
};

export const SUPPORT_USER_ENDED_TEXT =
  '지원 접속을 끝냈습니다. 이 탭을 닫아 주세요.';

/** 끝 화면 문구. reason 이 USER(배너 [종료])면 「끝냈습니다」, 그 밖은 「끝났습니다(사유)」 */
export const supportEndedText = (reason) =>
  reason === 'USER'
    ? SUPPORT_USER_ENDED_TEXT
    : `지원 접속이 끝났습니다(${
        SUPPORT_END_REASON_TEXT[reason] || '사유 알 수 없음'
      }). 이 탭을 닫아 주세요.`;

/** 끝 시각(초·밀리초 숫자 또는 ISO) → KST "M월 D일 오전/오후 h:mm", 읽을 수 없으면 "". 최대 8시간이라 자정을 넘기면 시각만으론 헷갈려 날짜를 붙임(11-01) */
export const kstClock = (expiresAt) => {
  if (expiresAt == null || expiresAt === '') return '';
  const n =
    typeof expiresAt === 'number'
      ? expiresAt < 1e12
        ? expiresAt * 1000
        : expiresAt
      : Date.parse(expiresAt);
  if (!Number.isFinite(n)) return '';
  // ponytail: KST 는 서머타임이 없어 +9시간 고정. 브라우저 Intl 글자 차이를 피하려고 직접 만듦
  const d = new Date(n + 9 * 3600 * 1000);
  const p2 = (v) => String(v).padStart(2, '0');
  const h = d.getUTCHours();
  const ampm = h < 12 ? '오전' : '오후';
  const hm = `${ampm} ${h % 12 || 12}:${p2(d.getUTCMinutes())}`;
  return `${d.getUTCMonth() + 1}월 ${d.getUTCDate()}일 ${hm}`;
};

/**
 * 배너 문구(계약 SUPPORT 5, 11-01 이름표 붙임): 「서티라이프 지원으로 접속 중 · 고객사 ○○병원 · 접속한 사람 정다운 · 10월 27일 오후 6:00 자동 종료」,
 * 리셀러면 「{actorOrg} 지원으로 접속 중 · …」. 빈 칸은 이름표째 뺌.
 * session = 교환 응답의 session(companyName·actorName·actorOrg·via·expiresAt). [접속 끝내기] 버튼은 화면이 붙임.
 */
export const supportBannerText = (session) => {
  if (!session) return '';
  const head =
    session.via === 'reseller_support'
      ? `${session.actorOrg || '리셀러'} 지원으로 접속 중`
      : '서티라이프 지원으로 접속 중';
  const end = kstClock(session.expiresAt);
  return [
    head,
    session.companyName && `고객사 ${session.companyName}`,
    session.actorName && `접속한 사람 ${session.actorName}`,
    end && `${end} 자동 종료`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * 지원 탭 갱신(계약 SUPPORT 9): POST /auth/support/refresh {refreshToken} → {accessToken, refreshToken, expiresIn}.
 * 동시에 여러 401 이 와도 한 번만 부르고(같은 Promise), 쿠키 /auth/refresh 는 부르지 않는다.
 * 실패면 {ended: 사유} 로 끝난다(401 SUPPORT_SESSION_ENDED 의 reason, 그 밖은 ENDED) — 부른 쪽이 끝 화면으로.
 * @param post (path, body) => Promise<{data}>   store tokenStore(지원 탭이 쓰는 함수들)
 */
export function createSupportRefresher({ post, store }) {
  let pending = null;
  return function refreshSupport() {
    if (pending) return pending;
    pending = (async () => {
      const refreshToken = store.getRefreshToken();
      if (!refreshToken) return { ended: 'NO_SESSION' };
      try {
        const { data } = await post('/auth/support/refresh', { refreshToken });
        if (!data || !data.accessToken) return { ended: 'ENDED' };
        store.setAccessToken(data.accessToken);
        if (data.refreshToken) store.setRefreshToken(data.refreshToken);
        return { accessToken: data.accessToken };
      } catch (error) {
        const d = error?.response?.data || {};
        return {
          ended:
            d.code === 'SUPPORT_SESSION_ENDED' ? d.reason || 'ENDED' : 'ENDED',
        };
      }
    })().finally(() => {
      pending = null;
    });
    return pending;
  };
}

/**
 * 리셀러 콘솔 지원 탭에서 숨기는 것(서버가 서브 계정에 거절하는 것만 — 그 밖은 리셀러 대표와 같음).
 * mySecurity 내 계정 보안·2단계 인증 · passwordChange 비밀번호 변경 · twoFactorPrompt 2차 인증 등록 안내.
 * 고객사로 「서브 관리자로 접속」·고객사 동의 스위치는 지원 탭에서도 됨(서버가 바깥 지원 세션의 사람으로 reseller_support 를 엶, D2 11-01)
 */
export const SUPPORT_TAB_BLOCKED = [
  'mySecurity',
  'passwordChange',
  'twoFactorPrompt',
];

/** 지원 탭에서 이 기능을 멈추나 */
export const blockedInSupportTab = (feature, supportTab) =>
  !!supportTab && SUPPORT_TAB_BLOCKED.includes(feature);

/** 사이드 메뉴 「보안설정」 묶음(암호 변경·내 계정 보안·2단계 인증) 화면 — 메뉴는 mySecurity 로 통째 숨김 */
export const SECURITY_MENU_PATHS = [
  '/security/change-password',
  '/security/my-account',
  '/two-factor-auth/settings',
];

/** 지원 탭에서 주소로 연 보안설정 화면인가 — 메뉴 숨김과 같은 blockedInSupportTab('mySecurity')(REDIRECT-REQ 1) */
export const blockedPathInSupportTab = (path, supportTab) =>
  blockedInSupportTab('mySecurity', supportTab) &&
  SECURITY_MENU_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

/**
 * 배너의 30초 확인 — 동의 철회·잠금 뒤 가만히 있는 탭도 끝 화면으로(서버 판정 캐시 30초 + 확인 30초 ≈ 1분).
 * x-support-probe = 쉬는 시간(30분)을 되돌리지 않음, _retry = 토큰을 갱신하지 않음.
 * 끝났으면(401 SUPPORT_SESSION_ENDED) axios 응답 처리기가 끝 화면으로 보냄. 다른 실패는 조용히 넘김.
 */
/**
 * 쉬는 시간(30분)은 이 신호로만 되돌린다(서버 POST /auth/support/activity — 배너 확인·토큰 갱신·화면이 혼자 부르는 요청은 활동 아님).
 * 지원 탭에서 사람이 누르거나 입력하면 1분에 1번까지 보낸다. 끝났으면(401) axios 응답 처리기가 끝 화면으로 보냄.
 */
export const SUPPORT_ACTIVITY_MS = 60000;
export const SUPPORT_ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
];
export const supportActivityReporter = (post, now = () => Date.now()) => {
  let last = -Infinity;
  return () => {
    const t = now();
    if (t - last < SUPPORT_ACTIVITY_MS) return false;
    last = t;
    Promise.resolve()
      .then(() =>
        post('/auth/support/activity', null, { showErrorNotice: false })
      )
      .catch(() => undefined);
    return true;
  };
};

export const SUPPORT_PROBE_MS = 30000;
export const supportProbe = (get) =>
  Promise.resolve()
    .then(() =>
      get('/auth/introspect', {
        _retry: true,
        showErrorNotice: false,
        headers: { 'x-support-probe': '1' },
      })
    )
    .then(
      () => undefined,
      () => undefined
    );
